import * as React from 'react';
import { Body2, Image, Link, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import privacyIcon from '../media/privacyoptions.svg';
import { useCookieConsent } from '../modules/cookie-consent';

export function Footer() {
  const intl = useIntl();
  const { siteConsent } = useCookieConsent();
  const classes = useStyles();

  return (
    <footer className={classes.root}>
      <nav className={classes.nav}>
        <div role="list" className={mergeClasses(classes.links, classes.text)}>
          {intl.locale === 'it-IT' ? (
            // Italy-only accessibility link. Pre-translated and should not be sent through intl process.
            <div role="listitem">
              <Link
                className={classes.link}
                href="https://www.microsoft.com/it-it/accessibility/declarations"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Body2>Accessibilità</Body2>
              </Link>
            </div>
          ) : null}
          {intl.locale === 'fr-FR' ? (
            // French-only accessibility link. Pre-translated and should not be sent through intl process.
            <div role="listitem">
              <Link
                className={classes.link}
                href="https://www.microsoft.com/fr-fr/accessibility/accessibilite/accessibility-statement"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Body2>Accessibilité</Body2>
              </Link>
            </div>
          ) : null}
          {siteConsent?.isConsentRequired ? (
            <div role="listitem">
              <Link
                className={classes.link}
                onClick={(event) => {
                  event.preventDefault();
                  siteConsent?.manageConsent();
                }}
              >
                <Body2>{intl.formatMessage(manageCookiesLabel)}</Body2>
              </Link>
            </div>
          ) : null}
          <div role="listitem">
            <Link
              target="_blank"
              className={mergeClasses(classes.link, classes.privacyChoicesLink)}
              href="https://aka.ms/yourcaliforniaprivacychoices"
              rel="noopener noreferrer"
            >
              <Image
                className={classes.privacyChoicesIcon}
                src={privacyIcon}
                fit="contain"
                alt={intl.formatMessage(privacyIconLabel)}
              />
              <Body2 className={classes.text}>{intl.formatMessage(privacyChoicesLabel)}</Body2>
            </Link>
          </div>
          <div role="listitem">
            <Link
              className={classes.link}
              href="https://go.microsoft.com/fwlink/?LinkId=521839"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Body2>{intl.formatMessage(privacyLabel)}</Body2>
            </Link>
          </div>
          <div role="listitem">
            <Link
              className={classes.link}
              href="https://www.microsoft.com/en-US/legal/intellectualproperty/copyright"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Body2>{intl.formatMessage(termsLabel)}</Body2>
            </Link>
          </div>
          <div role="listitem">
            <Link
              className={classes.link}
              href="https://www.microsoft.com/en-US/legal/intellectualproperty/trademarks/usage/general"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Body2>{intl.formatMessage(trademarksLabel)}</Body2>
            </Link>
          </div>
          <div role="listitem">
            <Link
              className={classes.link}
              href="https://support.microsoft.com/supportrequestform/354fe60a-ba6d-92ad-208a-6a41387aa9d8"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Body2>{intl.formatMessage(contactLabel)}</Body2>
            </Link>
          </div>
        </div>
      </nav>
    </footer>
  );
}

const manageCookiesLabel = defineMessage({
  defaultMessage: 'Manage Cookies',
  description: 'footer manage cookies link text',
});
const privacyLabel = defineMessage({
  defaultMessage: 'Privacy & Cookies',
  description: 'footer privacy link text',
});
const privacyChoicesLabel = defineMessage({
  defaultMessage: 'Your Privacy Choices',
  description: 'footer your privacy choices link text',
});
const termsLabel = defineMessage({
  defaultMessage: 'Terms of Use',
  description: 'footer terms link text',
});
const trademarksLabel = defineMessage({
  defaultMessage: 'Trademarks',
  description: 'footer trademarks link text',
});
const contactLabel = defineMessage({
  defaultMessage: 'Contact Us',
  description: 'footer contact link text',
});
const privacyIconLabel = defineMessage({
  defaultMessage: 'Privacy icon',
  description: 'icon aria label',
});

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorBrandBackground,
  },
  nav: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    ...shorthands.margin('14px', '60px', '14px', '182px'),
    '@media screen and (max-width: 859px)': {
      justifyContent: 'flex-start',
      ...shorthands.margin('24px', '16px'),
    },
  },
  links: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    ...shorthands.gap('36px'),
    '@media screen and (max-width: 859px)': {
      flexDirection: 'column',
      ...shorthands.gap('12px'),
    },
  },
  link: {
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '&:focus-visible': {
      outline: '1.5px solid', // Customize the box style as needed
      outlineOffset: '1px',
      textDecoration: 'none', // Remove default underline
      boxShadow: '0 0 0 1px',
    },
  },
  privacyChoicesLink: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalMNudge),
  },
  privacyChoicesIcon: {
    width: '43px',
  },
  text: {
    textAlign: 'end',
    '@media screen and (max-width: 859px)': {
      textAlign: 'start',
    },
  },
});
